import { useCallback, useContext, useMemo } from 'react';

import { useServiceFeatures } from 'hooks/useServiceContext';
import { CurrencyOption } from 'types/enums/CurrencyOptions';

import { QuickQuoteContext } from '../context/QuickQuoteContext';
import {
  convertDisplayToUsd,
  convertUsdForDisplay,
  formatAmountRange,
  formatAmountRangeWithSymbol,
  formatAmountWithCurrency,
  formatPerMileAmount,
  formatPerMileAmountNumber,
  formatPerMileAmountRange,
  formatPerMileAmountRangeNumber,
  formatPerMileRateNumber,
  formatUsdAmount,
  getCurrencySymbol,
  perMileRate,
} from '../utils/currencyFormatting';

export function useQuickQuoteCurrency() {
  const { quickQuoteConfig } = useServiceFeatures();
  const quickQuoteContext = useContext(QuickQuoteContext);

  const isEnabled =
    quickQuoteConfig?.isQuickQuoteCurrencyToggleEnabled === true;

  const selectedCurrency =
    quickQuoteContext?.quoteCurrency ?? CurrencyOption.USD;

  const toDisplayAmount = useCallback(
    (usdAmount: number): number => {
      if (!isEnabled) {
        return usdAmount;
      }

      return convertUsdForDisplay(usdAmount, selectedCurrency);
    },
    [isEnabled, selectedCurrency]
  );

  const fromDisplayAmount = useCallback(
    (displayAmount: number): number => {
      if (!isEnabled) {
        return displayAmount;
      }

      return convertDisplayToUsd(displayAmount, selectedCurrency);
    },
    [isEnabled, selectedCurrency]
  );

  const formatAmount = useCallback(
    (amount: number, maxFractionDigits = 0): string => {
      if (!isEnabled) {
        return formatUsdAmount(amount, maxFractionDigits);
      }

      return formatAmountWithCurrency(
        toDisplayAmount(amount),
        selectedCurrency,
        maxFractionDigits
      );
    },
    [isEnabled, selectedCurrency, toDisplayAmount]
  );

  const formatRange = useCallback(
    (low: number, high: number, maxFractionDigits = 0) => {
      if (!isEnabled) {
        return formatAmountRange(low, high, formatUsdAmount, maxFractionDigits);
      }

      return formatAmountRangeWithSymbol(
        toDisplayAmount(low),
        toDisplayAmount(high),
        selectedCurrency,
        maxFractionDigits
      );
    },
    [isEnabled, selectedCurrency, toDisplayAmount]
  );

  const formatPerMileRate = useCallback(
    (usdPerMile: number, maxFractionDigits = 2) => {
      const displayRate = toDisplayAmount(usdPerMile);

      if (!isEnabled) {
        return `$${formatPerMileRateNumber(displayRate, maxFractionDigits)}`;
      }

      return formatPerMileRateNumber(displayRate, maxFractionDigits);
    },
    [isEnabled, toDisplayAmount]
  );

  const formatPerMileLabel = useCallback(
    (value: number, distanceMiles: number) => {
      const displayRate = perMileRate(toDisplayAmount(value), distanceMiles);

      if (!isEnabled) {
        return formatPerMileAmount(value, distanceMiles, formatUsdAmount);
      }

      return formatPerMileAmountNumber(displayRate);
    },
    [isEnabled, toDisplayAmount]
  );

  const formatPerMileRange = useCallback(
    (low: number, high: number, distanceMiles: number) => {
      if (!isEnabled) {
        return formatPerMileAmountRange(
          low,
          high,
          distanceMiles,
          formatUsdAmount
        );
      }

      return formatPerMileAmountRangeNumber(
        perMileRate(toDisplayAmount(low), distanceMiles),
        perMileRate(toDisplayAmount(high), distanceMiles)
      );
    },
    [isEnabled, toDisplayAmount]
  );

  const inputPrefix = useMemo(() => {
    if (!isEnabled) {
      return '$';
    }

    return getCurrencySymbol(selectedCurrency);
  }, [isEnabled, selectedCurrency]);

  return {
    isEnabled,
    selectedCurrency,
    setSelectedCurrency: quickQuoteContext?.setQuoteCurrency,
    toDisplayAmount,
    fromDisplayAmount,
    formatAmount,
    formatRange,
    formatPerMileRate,
    formatPerMileLabel,
    formatPerMileRange,
    inputPrefix,
  };
}
